import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Phone, Clock, Shield, CheckCircle, Key, Zap, Droplet, Wind, Square, AlertCircle } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import StickyCallButton from '@/components/StickyCallButton';
import PageSEO from '@/components/PageSEO';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CityServicePage = () => {
  const { citySlug, serviceSlug } = useParams();
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCity = async () => {
      try {
        const response = await axios.get(`${API}/cities/${citySlug}`);
        setCity(response.data);
      } catch (error) {
        console.error('Fehler beim Laden der Stadt:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCity();
  }, [citySlug]);

  const serviceData = {
    'schluesseldienst': {
      icon: Key,
      title: 'Schlüsseldienst & Türöffnung',
      description: 'Professionelle Türöffnung ohne Schäden',
      content: {
        intro: 'Ausgesperrt? Schlüssel verloren? Unser Schlüsseldienst ist schnellstmöglich vor Ort und öffnet Ihre Tür fachgerecht.',
        services: [
          'Türöffnung bei verlorenen oder vergessenen Schlüsseln',
          'Schlüssel im Schloss abgebrochen – professionelle Extraktion',
          'Zylinderwechsel und Schlosswartung',
          'Sicherheitsberatung und Einbruchschutz',
          'Notfall-Türöffnung nach Einbruch'
        ],
        features: [
          { title: 'Beschädigungsfreie Öffnung', desc: 'In über 90% der Fälle ohne Schäden an Tür und Schloss' },
          { title: 'Schnelle Reaktion', desc: 'In der Regel innerhalb von 30-60 Minuten vor Ort' },
          { title: 'Transparente Abläufe', desc: 'Faire und nachvollziehbare Arbeitsweise' }
        ],
        faqs: [
          { q: `Wie schnell ist der Schlüsseldienst in ${city?.name}?`, a: `Unser Schlüsseldienst-Team bemüht sich, schnellstmöglich zu Ihnen in ${city?.name} zu kommen. Je nach Stadtteil und Verkehrslage sind wir in der Regel innerhalb von 30-60 Minuten vor Ort.` },
          { q: 'Wird die Tür beschädigt?', a: 'Wir arbeiten mit professionellem Werkzeug und modernen Techniken. In den meisten Fällen können wir Türen ohne Beschädigung öffnen.' },
          { q: 'Was tun bei Aussperrung?', a: 'Bleiben Sie ruhig und rufen Sie uns unter 0231 39083687 an. Wir beraten Sie am Telefon und schicken einen Techniker.' }
        ]
      }
    },
    'elektro-notdienst': {
      icon: Zap,
      title: 'Elektro-Notdienst',
      description: 'Schnelle Hilfe bei Stromausfällen',
      content: {
        intro: 'Stromausfall, defekte Sicherung oder elektrischer Notfall? Unser Elektro-Notdienst ist rund um die Uhr für Sie im Einsatz.',
        services: [
          'Behebung von Stromausfällen und Kurzschlüssen',
          'Austausch defekter Sicherungen und FI-Schalter',
          'Reparatur von Steckdosen, Schaltern und Beleuchtung',
          'Fehlersuche in elektrischen Anlagen',
          'Notfall-Maßnahmen bei elektrischen Gefahren'
        ],
        features: [
          { title: 'Qualifizierte Elektriker', desc: 'Geprüfte Fachkräfte mit allen erforderlichen Zulassungen' },
          { title: '24/7 Verfügbar', desc: 'Auch nachts, an Wochenenden und Feiertagen' },
          { title: 'Sicherheit First', desc: 'Professionelle Maßnahmen zur Gefahrenabwehr' }
        ],
        faqs: [
          { q: `Wie schnell kommt der Elektro-Notdienst in ${city?.name}?`, a: `Unser Elektro-Notdienst bemüht sich, bei elektrischen Notfällen schnellstmöglich in ${city?.name} vor Ort zu sein. Die Anfahrtszeit beträgt in der Regel 30-60 Minuten.` },
          { q: 'Was tun bei Stromausfall?', a: 'Prüfen Sie zunächst, ob nur Ihre Wohnung betroffen ist. Kontrollieren Sie den Sicherungskasten. Bei Unsicherheit rufen Sie uns an.' },
          { q: 'Sind die Elektriker qualifiziert?', a: 'Ja, alle unsere Elektriker verfügen über die erforderlichen Qualifikationen und Zulassungen für Elektroarbeiten.' }
        ]
      }
    },
    'sanitaer-heizung': {
      icon: Droplet,
      title: 'Sanitär & Heizung',
      description: 'Sofortmaßnahmen bei Wasserschaden',
      content: {
        intro: 'Rohrbruch, Wasserschaden oder Heizungsausfall? Unser Sanitär- und Heizungsnotdienst reagiert schnell und minimiert Folgeschäden.',
        services: [
          'Notfall-Maßnahmen bei Rohrbruch und Wasserschaden',
          'Reparatur undichter Leitungen und Armaturen',
          'Heizungsausfall beheben – auch im Winter',
          'Verstopfte Abflüsse und Toiletten befreien',
          'Warmwasser-Notversorgung'
        ],
        features: [
          { title: 'Schnelle Schadensbegrenzung', desc: 'Sofortmaßnahmen zur Minimierung von Wasserschäden' },
          { title: 'Erfahrene Techniker', desc: 'Professionelle Sanitär- und Heizungsfachkräfte' },
          { title: 'Notfall-Equipment', desc: 'Vollständig ausgestattete Einsatzfahrzeuge' }
        ],
        faqs: [
          { q: `Wie schnell hilft der Sanitär-Notdienst in ${city?.name}?`, a: `Bei Wasserschäden und Heizungsausfällen ist Schnelligkeit entscheidend. Unser Sanitär-Notdienst bemüht sich, innerhalb von 30-60 Minuten in ${city?.name} vor Ort zu sein.` },
          { q: 'Wo finde ich den Hauptwasserhahn?', a: 'Der Hauptwasserhahn befindet sich meist im Keller, in der Nähe der Wasseruhr oder im Hausanschlussraum.' },
          { q: 'Zahlt die Versicherung?', a: 'In vielen Fällen übernimmt die Hausrat- oder Wohngebäudeversicherung Notdiensteinsätze. Wir stellen Ihnen alle nötigen Unterlagen aus.' }
        ]
      }
    },
    'rohrreinigung': {
      icon: Wind,
      title: 'Rohrreinigung',
      description: 'Professionelle Beseitigung von Verstopfungen',
      content: {
        intro: 'Verstopfte Abflüsse oder überlaufende Toiletten? Unser Rohrreinigungsdienst beseitigt Verstopfungen schnell und nachhaltig.',
        services: [
          'Beseitigung von Verstopfungen in Küche, Bad und WC',
          'Reinigung von Abwasserleitungen mit Spirale oder Hochdruck',
          'Beseitigung von Rückstau und Überschwemmungen',
          'Kanalinspektion mit Kamera',
          'Präventive Wartung und Reinigung'
        ],
        features: [
          { title: 'Professionelles Equipment', desc: 'Rohrreinigungsspiralen und Hochdruckreiniger' },
          { title: 'Nachhaltige Lösung', desc: 'Gründliche Reinigung statt temporärer Behelfslösung' },
          { title: 'Umweltschonend', desc: 'Mechanische Reinigung ohne aggressive Chemikalien' }
        ],
        faqs: [
          { q: `Wie lange dauert eine Rohrreinigung in ${city?.name}?`, a: `Die Dauer hängt von Art und Ort der Verstopfung ab. In ${city?.name} dauert eine Rohrreinigung in der Regel zwischen 30 Minuten und 2 Stunden.` },
          { q: 'Wie entstehen Verstopfungen?', a: 'Häufige Ursachen sind Haare, Seifenreste, Fette, Speisereste oder Fremdk\u00f6rper in den Rohren.' },
          { q: 'Kann ich selbst etwas tun?', a: 'Bei leichten Verstopfungen können Sie es mit einer Saugglocke versuchen. Bei hartnäckigen Verstopfungen: Rufen Sie uns an.' }
        ]
      }
    },
    'glas-notdienst': {
      icon: Square,
      title: 'Glas-Notdienst',
      description: 'Schnelle Notverglasung',
      content: {
        intro: 'Zerbrochene Fenster oder Glastüren stellen ein Sicherheitsrisiko dar. Unser Glas-Notdienst sichert Ihr Objekt schnellstmöglich.',
        services: [
          'Notverglasung von Fenstern und Türen',
          'Sofortmaßnahmen nach Einbruch oder Vandalismus',
          'Austausch von Isolier- und Sicherheitsglas',
          'Sicherung von Schaufenstern und Vitrinen',
          'Beseitigung von Glasscherben'
        ],
        features: [
          { title: 'Schnelle Sicherung', desc: 'Sofortige Notverglasung zum Schutz Ihres Objekts' },
          { title: 'Einbruchschutz', desc: 'Verhinderung weiterer Schäden und Einbrüche' },
          { title: 'Fachgerechte Arbeit', desc: 'Professionelle Verglasung durch erfahrene Glaser' }
        ],
        faqs: [
          { q: `Wie schnell kommt der Glas-Notdienst in ${city?.name}?`, a: `Bei zerbrochenen Fenstern ist schnelles Handeln wichtig. Unser Glas-Notdienst bemüht sich, innerhalb von 30-60 Minuten in ${city?.name} vor Ort zu sein.` },
          { q: 'Was ist eine Notverglasung?', a: 'Eine Notverglasung ist eine vorübergehende Sicherung der Öffnung, um Ihr Objekt vor Einbruch und Wetter zu schützen.' },
          { q: 'Zahlt die Versicherung?', a: 'In der Regel übernimmt die Hausrat- oder Glasversicherung solche Schäden. Wir stellen Ihnen alle nötigen Unterlagen aus.' }
        ]
      }
    },
    'einbruchschutz': {
      icon: Shield,
      title: 'Einbruchschutz & Notverglasung',
      description: 'Sicherheitsmaßnahmen nach Einbrüchen',
      content: {
        intro: 'Nach einem Einbruch oder zur Prävention: Wir sichern Ihr Objekt mit professionellen Einbruchschutzmaßnahmen.',
        services: [
          'Sofortmaßnahmen nach Einbruch',
          'Installation von Sicherheitsschlössern und Panzerriegeln',
          'Montage von Fenstersicherungen',
          'Sicherheitsberatung für Privat- und Gewerbekunden',
          'Türspione und Schutzbeschläge'
        ],
        features: [
          { title: 'Professionelle Sicherung', desc: 'Mechanische Sicherheitstechnik nach DIN-Norm' },
          { title: 'Individuelle Beratung', desc: 'Maßgeschneiderte Sicherheitskonzepte' },
          { title: 'Schnelle Umsetzung', desc: 'Sofortmaßnahmen bei akuten Sicherheitsproblemen' }
        ],
        faqs: [
          { q: `Wie schnell kann Einbruchschutz in ${city?.name} installiert werden?`, a: `Bei akuten Sicherheitsproblemen nach einem Einbruch können wir Sofortmaßnahmen in ${city?.name} innerhalb von 30-60 Minuten durchführen.` },
          { q: 'Was tun nach einem Einbruch?', a: 'Polizei rufen (110), nichts anfassen (Spurensicherung), Schäden dokumentieren und unseren Notdienst für Sofortsicherung kontaktieren.' },
          { q: 'Gibt es Förderungen?', a: 'Ja, die KfW bietet Förderprogramme für Einbruchschutzmaßnahmen an. Wir informieren Sie gerne.' }
        ]
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center py-20">
          <p className="text-gray-500">Lade Daten...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!city) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex flex-col items-center justify-center py-20">
          <p className="text-gray-500 text-lg mb-4">Stadt nicht gefunden</p>
          <Link to="/einsatzgebiete" className="text-[#0066cc] hover:underline">Zurück zur Übersicht</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const service = serviceData[serviceSlug] || serviceData['schluesseldienst'];
  const Icon = service.icon;

  // JSON-LD Schema
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": service.title,
    "areaServed": {
      "@type": "City",
      "name": city.name,
      "addressRegion": "Nordrhein-Westfalen",
      "addressCountry": "DE"
    },
    "provider": {
      "@type": "LocalBusiness",
      "name": `Handwerkernotdienst NRW in ${city.name}`,
      "telephone": "+49-231-39083687",
      "email": "info@handwerkernotdienst.nrw",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Dortmund",
        "addressRegion": "Nordrhein-Westfalen",
        "addressCountry": "DE"
      }
    },
    "availableChannel": {
      "@type": "ServiceChannel",
      "servicePhone": {
        "@type": "ContactPoint",
        "telephone": "+49-231-39083687",
        "availableLanguage": "de",
        "hoursAvailable": "Mo-Su 00:00-23:59"
      }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <PageSEO
        title={`${service.title} in ${city.name} | 24/7 Notdienst ☎ 0231 39083687`}
        description={`${service.title} in ${city.name}: ${service.description}. 24/7 Notdienst für alle Stadtteile. Schnelle Hilfe vor Ort ☎ 0231 39083687. Erfahrene Fachkräfte.`}
        canonicalUrl={`https://handwerkernotdienst.nrw/einsatzgebiete/${city.slug}/${serviceSlug}`}
        breadcrumbs={[
          { name: 'Start', url: 'https://handwerkernotdienst.nrw/' },
          { name: 'Einsatzgebiete', url: 'https://handwerkernotdienst.nrw/einsatzgebiete' },
          { name: city.name, url: `https://handwerkernotdienst.nrw/einsatzgebiete/${city.slug}` },
          { name: service.title, url: `https://handwerkernotdienst.nrw/einsatzgebiete/${city.slug}/${serviceSlug}` }
        ]}
        schema={schema}
      />
      
      <Header />
      <StickyCallButton />

      {/* JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#e8f4f8] to-white py-16" data-testid="city-service-hero">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-sm text-gray-500 mb-6">
            <Link to="/" className="hover:text-[#0066cc]">Start</Link>
            <span>/</span>
            <Link to="/einsatzgebiete" className="hover:text-[#0066cc]">Einsatzgebiete</Link>
            <span>/</span>
            <Link to={`/einsatzgebiete/${city.slug}`} className="hover:text-[#0066cc]">{city.name}</Link>
            <span>/</span>
            <span className="text-gray-900">{service.title}</span>
          </div>

          <div className="flex items-start space-x-4 mb-6">
            <div className="w-16 h-16 bg-[#0066cc] rounded-xl flex items-center justify-center flex-shrink-0">
              <Icon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3" data-testid="city-service-title">
                {service.title} in {city.name}
              </h1>
              <p className="text-lg text-gray-600">
                24/7 Notdienst – Schnelle Hilfe vor Ort
              </p>
            </div>
          </div>

          <a href="tel:+4923139083687" data-testid="city-service-call-button">
            <Button size="lg" className="bg-[#e63946] hover:bg-[#d62828] text-white px-8 rounded-full font-bold">
              <Phone className="w-5 h-5 mr-2" />
              Jetzt anrufen: 0231 39083687
            </Button>
          </a>
        </div>
      </section>

      {/* Content */}
      <section className="py-16" data-testid="city-service-content">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              {service.title} in {city.name} – Ihr zuverlässiger Partner
            </h2>
            
            <p className="text-gray-700 leading-relaxed mb-6">
              {service.content.intro} {city.description} Unser erfahrenes Team kennt {city.name} und 
              die örtlichen Gegebenheiten und ist schnellstmöglich bei Ihnen vor Ort.
            </p>

            <div className="bg-[#e8f4f8] p-6 rounded-xl mb-8">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                <CheckCircle className="w-5 h-5 text-[#0066cc] mr-2" />
                Schnelle Hilfe in {city.name}
              </h4>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Verfügbarkeit:</strong> 24 Stunden am Tag, 7 Tage die Woche</li>
                <li><strong>Einsatzgebiet:</strong> Gesamtes Stadtgebiet {city.name} und alle Stadtteile</li>
                <li><strong>Reaktionszeit:</strong> In der Regel 30-60 Minuten</li>
                <li><strong>Kontakt:</strong> Telefon 0231 39083687</li>
              </ul>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
              Unsere {service.title}-Leistungen in {city.name}
            </h3>
            <ul className="space-y-3 mb-8">
              {service.content.services.map((item, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
              Warum Handwerkernotdienst NRW in {city.name}?
            </h3>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {service.content.features.map((feature, index) => (
                <div key={index} className="bg-gray-50 p-6 rounded-xl">
                  <h4 className="font-semibold text-gray-900 mb-2">{feature.title}</h4>
                  <p className="text-gray-600 text-sm">{feature.desc}</p>
                </div>
              ))}
            </div>

            {/* Districts */}
            {city.districts && city.districts.length > 0 && (
              <div className="mt-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {service.title} in allen Stadtteilen von {city.name}
                </h3>
                <p className="text-gray-700 mb-6">
                  Wir sind in allen Stadtteilen von {city.name} für Sie im Einsatz:
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {city.districts.map((district, index) => (
                    <div key={index} className="bg-gray-50 px-4 py-2 rounded-lg text-gray-700 text-sm">
                      {district.name}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* FAQ */}
            <div className="mt-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Häufige Fragen zu {service.title} in {city.name}
              </h2>
              <Accordion type="single" collapsible className="space-y-4">
                {service.content.faqs.map((faq, index) => (
                  <AccordionItem
                    key={index}
                    value={`faq-${index}`}
                    className="bg-gray-50 rounded-lg px-6 border border-gray-200"
                    data-testid={`city-service-faq-${index}`}
                  >
                    <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-[#0066cc] py-4">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 pb-4">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

            {/* CTA */}
            <div className="mt-12 bg-[#0066cc] rounded-2xl p-8 text-white text-center">
              <h3 className="text-3xl font-bold mb-4">{service.title} in {city.name} benötigt?</h3>
              <p className="text-lg mb-6 opacity-90">
                Rufen Sie uns jetzt an – wir sind rund um die Uhr für Sie da!
              </p>
              <a href="tel:+4923139083687" data-testid="city-service-cta-button">
                <Button size="lg" className="bg-[#e63946] hover:bg-[#d62828] text-white text-xl px-10 py-6 rounded-full font-bold">
                  <Phone className="w-6 h-6 mr-2" />
                  0231 39083687
                </Button>
              </a>
            </div>

            <div className="mt-8">
              <Link
                to={`/einsatzgebiete/${city.slug}`}
                className="text-[#0066cc] hover:underline font-semibold"
                data-testid="back-to-city-link"
              >
                ← Zurück zu allen Leistungen in {city.name}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CityServicePage;
